import { addWarning } from "@wayflyer/blackbox-fe";
import type { FundingApplicationSchemas } from "api-types";
import { createContext, useContext } from "react";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import {
  CONNECT_APP_ROUTE_PATHS,
  NAVIGATION_PATHS,
  ORDERED_CONNECTOR_SLUGS,
  ORDERED_CONNECTOR_SLUGS_WITH_PAYMENTS_LEGACY,
} from "../../components/constants";
import type { FormattedFundingApplicationScreen } from "../../queries/funding-application/queries";
import { useFundingApplicationStateMutation } from "../../queries/funding-application/queries";
import { UploadDocumentType, type BusinessModelData } from "../../types";
import {
  isAccountingUploadRequired,
  isCashflowRequired,
  isDebtAgreementRequired,
  isGoogleAnalyticsRequired,
  isPoolBancarioRequired,
  isRssBusinessModel,
  isSusaRequired,
} from "../../utils/funding-journey";

import { MESSAGES, NEXT_BUTTON_MESSAGES } from "./message-definitions";

export enum PAGE_TYPE {
  SELECT_PLATFORMS = "SELECT_PLATFORMS",
  APPLICATION_OVERVIEW = "APPLICATION_OVERVIEW",
  CONNECTORS = "CONNECTORS",
  BANKING = "BANKING",
  BANK_STATEMENTS_UPLOAD = "BANK_STATEMENTS_UPLOAD",
  DOCUMENTS = "DOCUMENTS",
  SUMMARY = "SUMMARY",
  CREDIT_CHECK_CONSENT = "CREDIT_CHECK_CONSENT",
}

const pagePaths = {
  [PAGE_TYPE.SELECT_PLATFORMS]: NAVIGATION_PATHS.CONNECTIONS,
  [PAGE_TYPE.APPLICATION_OVERVIEW]: NAVIGATION_PATHS.APPLICATION_OVERVIEW,
  [PAGE_TYPE.CONNECTORS]: NAVIGATION_PATHS.ADD_CONNECTION,
  [PAGE_TYPE.BANKING]: NAVIGATION_PATHS.BANKING,
  [PAGE_TYPE.BANK_STATEMENTS_UPLOAD]: NAVIGATION_PATHS.BANK_STATEMENTS_UPLOAD,
  [PAGE_TYPE.DOCUMENTS]: `/${CONNECT_APP_ROUTE_PATHS.UPLOADS}`,
  [PAGE_TYPE.SUMMARY]: NAVIGATION_PATHS.SUMMARY,
  [PAGE_TYPE.CREDIT_CHECK_CONSENT]: NAVIGATION_PATHS.CREDIT_CHECK_CONSENT,
};

export const composePath = (pageType: PAGE_TYPE, pageStep?: string): string => {
  if (pageStep) {
    if (pageType === PAGE_TYPE.CONNECTORS) {
      return `${pagePaths[pageType]}?platform=${pageStep}`;
    } else if (pageType === PAGE_TYPE.DOCUMENTS) {
      return `${pagePaths[pageType]}?document=${pageStep}`;
    }
  }

  return pagePaths[pageType];
};

export interface StepContext {
  currentPage: PAGE_TYPE;
  currentStep?: string; // e.g. connector slug or document type
}

export interface FundingApplicationStepContext extends StepContext {
  previousPage?: PAGE_TYPE;
  previousStep?: string;

  nextPage?: PAGE_TYPE;
  nextStep?: string;

  connectorSlugs: string[];
  documents: string[];
}

export interface NavigationProps {
  nextPath?: string;
  previousPath?: string;
  nextPageType?: PAGE_TYPE;
  nextButtonLabel: string;
  navigateWithStateUpdate: (path: string) => Promise<void>;
  navigate: (path: string) => void;
  currentStepIndex: number;
  stepString: string;
}

interface ApplicationJourneyPage {
  page: PAGE_TYPE;
  steps?: string[];
}

// For new config journeys (without APPLICATION_OVERVIEW): SELECT_PLATFORMS + SUMMARY = 2
const NOT_COUNTED_PAGES_NEW_CONFIG = 2;
// For legacy journeys (with APPLICATION_OVERVIEW): SELECT_PLATFORMS + APPLICATION_OVERVIEW + SUMMARY = 3
// Used for SBL/RSS/Ecommerce non-stage journeys that retain the application overview page.
const NOT_COUNTED_PAGES_LEGACY = 3;

/**
 * For the current page (and step) in the navigation flow, find the previous path
 * @param navigationFlow
 * @param currentPageIndex
 * @param currentStep
 * @returns
 */
export const findPreviousPath = (
  navigationFlow: ApplicationJourneyPage[],
  currentPageIndex: number,
  currentStep?: string
): string => {
  if (currentPageIndex === 0) {
    return "";
  }
  const currentPage = navigationFlow[currentPageIndex];
  const previousPage = navigationFlow[currentPageIndex - 1];

  // if the current page has steps and not at the first step, go back to the previous step
  if (currentStep && currentPage.steps && currentPage.steps.length) {
    const currentStepIndex = currentPage.steps.findIndex((step) => step === currentStep);

    if (currentStepIndex < 0) {
      // this means something has gone wrong so log warning and return the previous stage
      addWarning(
        `Unknown step ${currentStep} passed to findPreviousPath for page ${currentPage.page}. Known steps are ${currentPage.steps}`
      );
      return composePath(
        previousPage.page,
        previousPage.steps && previousPage.steps.length ? previousPage.steps[previousPage.steps.length - 1] : ""
      );
    }
    if (currentStepIndex !== 0) {
      return composePath(currentPage.page, currentPage.steps[currentStepIndex - 1]);
    }
  }

  // if previous page has steps, go to the last step of the previous page
  return composePath(
    previousPage.page,
    previousPage.steps && previousPage.steps.length ? previousPage.steps[previousPage.steps.length - 1] : ""
  );
};

/**
 * For the current page (and step) in the navigation flow, find the next path
 * @param navigationFlow
 * @param currentPageIndex
 * @param currentStep
 * @returns
 */
export const findNextPath = (
  navigationFlow: ApplicationJourneyPage[],
  currentPageIndex: number,
  currentStep?: string
): string => {
  if (currentPageIndex === navigationFlow.length - 1) {
    return "";
  }
  const currentPage = navigationFlow[currentPageIndex];
  const nextPage = navigationFlow[currentPageIndex + 1];

  // if the current page has steps and not at the last step, go to the next step
  if (currentStep && currentPage.steps && currentPage.steps.length) {
    const currentStepIndex = currentPage.steps.findIndex((step) => step === currentStep);
    if (currentStepIndex < 0) {
      // this means something has gone wrong so log a warning. We can fall through as normal flow will take us to first step of current page
      addWarning(
        `Unknown step ${currentStep} passed to findNextPath for page ${currentPage.page}. Known steps are ${currentPage.steps}`
      );
    }
    if (currentStepIndex !== currentPage.steps.length - 1) {
      return composePath(currentPage.page, currentPage.steps[currentStepIndex + 1]);
    }
  }

  // if next page has steps, go to the first step of the next page
  return composePath(nextPage.page, nextPage.steps && nextPage.steps.length ? nextPage.steps[0] : "");
};

export const useFundingApplicationNavigation = ({
  currentPage,
  currentStep,
  startPagesCount = 2,
}: {
  currentPage: PAGE_TYPE;
  currentStep?: string;
  startPagesCount?: number;
}): NavigationProps => {
  const { navigate } = useSpaNavigate("FundingApp");
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const intl = useIntl();
  const applicationStateMutation = useFundingApplicationStateMutation(customerDomain);
  const navigationContext = useNavigationContext();

  //TODO: remove when doing MCA-3133, BE will determine post-login path at that point
  const navigateWithStateUpdate = (path: string) => {
    return applicationStateMutation
      .mutateAsync({ applicationState: "in_progress", applicationSubState: path })
      .then(() => navigate(path));
  };

  const currentNavigationPageIndex = navigationContext.navigationFlow.findIndex((step) => {
    return step.page === currentPage.valueOf();
  });

  // it's possible to not be able to get the current index if the state has been edited so default to first page of the journey
  // which will reset the subState when the user continues
  if (currentNavigationPageIndex < 0) {
    addWarning("Unable to determine current page in application flow", { currentPage, navigationContext });
    // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    window.location.href = `/${customerDomain}/funding/application${composePath(
      navigationContext.navigationFlow[0].page
    )}`;
  }

  const previousPath = findPreviousPath(navigationContext.navigationFlow, currentNavigationPageIndex, currentStep);
  const nextPath = findNextPath(navigationContext.navigationFlow, currentNavigationPageIndex, currentStep);
  const nextPageType = navigationContext.navigationFlow[currentNavigationPageIndex + 1]?.page;
  const nextPageSteps = navigationContext.navigationFlow[currentNavigationPageIndex + 1]?.steps;

  const getDocumentLabel = (documentType?: string): string => {
    if (!documentType) {
      return intl.formatMessage(NEXT_BUTTON_MESSAGES.documentsButton);
    }

    const documentLabelMap: Record<string, string> = {
      [UploadDocumentType.SUSA]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadSusa),
      [UploadDocumentType.POOL_BANCARIO]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadPoolBancario),
      [UploadDocumentType.CASH_FLOW_FORECAST]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadRevenueForecast),
      [UploadDocumentType.COMPANY_DEBT_AGREEMENT]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadDebtAgreement),
      [UploadDocumentType.MANAGEMENT_ACCOUNTS]: intl.formatMessage(
        NEXT_BUTTON_MESSAGES.documentUploadManagementAccounts
      ),
      [UploadDocumentType.PROFIT_AND_LOSS]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadProfitAndLoss),
      [UploadDocumentType.BALANCE_SHEET]: intl.formatMessage(NEXT_BUTTON_MESSAGES.documentUploadBalanceSheet),
    };

    return documentLabelMap[documentType] ?? intl.formatMessage(NEXT_BUTTON_MESSAGES.documentsButton);
  };

  const getNextButtonLabel = (): string => {
    if (!nextPageType) {
      return intl.formatMessage(NEXT_BUTTON_MESSAGES.continueLabel);
    }

    switch (nextPageType) {
      case PAGE_TYPE.CONNECTORS:
        return intl.formatMessage(NEXT_BUTTON_MESSAGES.platformsButton);
      case PAGE_TYPE.DOCUMENTS:
        return getDocumentLabel(nextPageSteps?.[0]);
      case PAGE_TYPE.CREDIT_CHECK_CONSENT:
        return intl.formatMessage(NEXT_BUTTON_MESSAGES.softCreditCheckButton);
      case PAGE_TYPE.SUMMARY:
        return intl.formatMessage(NEXT_BUTTON_MESSAGES.applicationReviewButton);
      default:
        return intl.formatMessage(NEXT_BUTTON_MESSAGES.continueLabel);
    }
  };

  const nextButtonLabel = getNextButtonLabel();

  const currentStepIndex = currentNavigationPageIndex - startPagesCount + 1;
  const stepString = intl.formatMessage(MESSAGES.stepperKickerLabel, {
    currentStep: currentStepIndex.toString(),
    totalSteps: navigationContext.totalSteps.toString(),
  });

  return {
    previousPath,
    nextPath,
    nextPageType,
    nextButtonLabel,
    navigateWithStateUpdate,
    navigate,
    currentStepIndex,
    stepString,
  };
};

export const LinearNavigationContext = createContext<NavigationContext | null>(null);

export const filterSelectedConnectors = (
  connectors: FundingApplicationSchemas.ConnectorPropertiesSchema[],
  connectorSlugsForJourney: string[]
) => connectors.filter((connector) => connectorSlugsForJourney.includes(connector.slug));

export const getRequiredDocsFromConfiguration = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  const docTypes: FundingApplicationSchemas.DocumentType[] = [];
  if (fundingApplicationConfiguration.documents) {
    fundingApplicationConfiguration.documents.forEach((doc) => {
      docTypes.push(doc.documentType);
    });
  }

  return docTypes;
};

export const getPlatformsForApplication = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  let requiredPlatforms: string[] = [];
  // Include any required connectors from configuration stages
  const connectorsForJourney = new Set([
    ...fundingApplication.selectedConnectorSlugs,
    ...(fundingApplicationConfiguration.revenue?.requiredConnectorSlugs ?? []),
    ...(fundingApplicationConfiguration.marketing?.requiredConnectorSlugs ?? []),
    ...(fundingApplicationConfiguration.accounting?.requiredConnectorSlugs ?? []),
  ]);
  if (connectorsForJourney) {
    requiredPlatforms = ORDERED_CONNECTOR_SLUGS.filter((slug) =>
      slug === "ga"
        ? connectorsForJourney.has(slug) && isGoogleAnalyticsRequired(Array.from(connectorsForJourney))
        : connectorsForJourney.has(slug)
    );
  }
  return requiredPlatforms;
};

export const calculateJourneyFromStages = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): NavigationContext => {
  // Connections live in the side panel; banking is the first step when configured.
  const journey: ApplicationJourneyPage[] = fundingApplicationConfiguration.banking
    ? [{ page: PAGE_TYPE.BANKING }]
    : [];
  if (fundingApplicationConfiguration.includePlatformSelection) {
    journey.push({ page: PAGE_TYPE.CONNECTORS });
  }

  const requiredDocTypes = getRequiredDocsFromConfiguration(fundingApplication, fundingApplicationConfiguration);
  if (requiredDocTypes.length) {
    journey.push({ page: PAGE_TYPE.DOCUMENTS, steps: requiredDocTypes });
  }

  if (fundingApplicationConfiguration.creditConsent) {
    journey.push({ page: PAGE_TYPE.CREDIT_CHECK_CONSENT });
  }

  journey.push({ page: PAGE_TYPE.SUMMARY });

  return { navigationFlow: journey, totalSteps: journey.length - NOT_COUNTED_PAGES_NEW_CONFIG };
};

export const calculateRssJourney = (
  fundingApplication: FormattedFundingApplicationScreen,
  businessModel: BusinessModelData,
  countryOfIncorporation?: string,
  uploadedDocuments?: FundingApplicationSchemas.ListDocumentsResponse[]
): NavigationContext => {
  const journey: ApplicationJourneyPage[] = [
    { page: PAGE_TYPE.SELECT_PLATFORMS },
    { page: PAGE_TYPE.APPLICATION_OVERVIEW },
  ];

  const docTypes: string[] = [];
  if (isPoolBancarioRequired(countryOfIncorporation)) {
    docTypes.push(UploadDocumentType.POOL_BANCARIO);
  } else if (isSusaRequired(countryOfIncorporation, fundingApplication)) {
    docTypes.push(UploadDocumentType.SUSA);
  }

  if (isAccountingUploadRequired(businessModel, fundingApplication, countryOfIncorporation)) {
    if (uploadedDocuments?.some((doc) => doc.documentType === UploadDocumentType.MANAGEMENT_ACCOUNTS)) {
      docTypes.push(UploadDocumentType.MANAGEMENT_ACCOUNTS);
    } else {
      docTypes.push(UploadDocumentType.PROFIT_AND_LOSS);
      docTypes.push(UploadDocumentType.BALANCE_SHEET);
    }
  }

  journey.push({ page: PAGE_TYPE.BANKING });

  if (docTypes.length) {
    journey.push({ page: PAGE_TYPE.DOCUMENTS, steps: docTypes });
  }

  if (fundingApplication.selectedConnectorSlugs.length) {
    const requiredPlatforms: string[] = ORDERED_CONNECTOR_SLUGS_WITH_PAYMENTS_LEGACY.filter((slug) =>
      slug === "ga"
        ? fundingApplication.selectedConnectorSlugs.includes(slug) &&
          isGoogleAnalyticsRequired(fundingApplication.selectedConnectorSlugs)
        : fundingApplication.selectedConnectorSlugs.includes(slug)
    );
    if (requiredPlatforms.length) {
      journey.push({ page: PAGE_TYPE.CONNECTORS, steps: requiredPlatforms });
    }
  }

  journey.push({ page: PAGE_TYPE.SUMMARY });

  return { navigationFlow: journey, totalSteps: journey.length - NOT_COUNTED_PAGES_LEGACY };
};

export const calculateEcommerceOrWholesaleJourney = (
  fundingApplication: FormattedFundingApplicationScreen,
  businessModel: BusinessModelData,
  countryOfIncorporation?: string,
  uploadedDocuments?: FundingApplicationSchemas.ListDocumentsResponse[]
) => {
  const journey: ApplicationJourneyPage[] = [
    { page: PAGE_TYPE.SELECT_PLATFORMS },
    { page: PAGE_TYPE.APPLICATION_OVERVIEW },
  ];

  const docTypes: string[] = [];
  if (isPoolBancarioRequired(countryOfIncorporation)) {
    docTypes.push(UploadDocumentType.POOL_BANCARIO);
  } else if (isSusaRequired(countryOfIncorporation, fundingApplication)) {
    docTypes.push(UploadDocumentType.SUSA);
  }
  if (isAccountingUploadRequired(businessModel, fundingApplication, countryOfIncorporation)) {
    if (uploadedDocuments?.some((doc) => doc.documentType === UploadDocumentType.MANAGEMENT_ACCOUNTS)) {
      docTypes.push(UploadDocumentType.MANAGEMENT_ACCOUNTS);
    } else {
      docTypes.push(UploadDocumentType.PROFIT_AND_LOSS);
      docTypes.push(UploadDocumentType.BALANCE_SHEET);
    }
  }
  if (isDebtAgreementRequired(fundingApplication, businessModel)) {
    docTypes.push(UploadDocumentType.COMPANY_DEBT_AGREEMENT);
  }
  if (isCashflowRequired(fundingApplication, businessModel)) {
    docTypes.push(UploadDocumentType.CASH_FLOW_FORECAST);
  }
  journey.push({ page: PAGE_TYPE.BANKING });

  if (docTypes.length) {
    journey.push({ page: PAGE_TYPE.DOCUMENTS, steps: docTypes });
  }

  if (fundingApplication.selectedConnectorSlugs.length) {
    const requiredPlatforms: string[] = ORDERED_CONNECTOR_SLUGS_WITH_PAYMENTS_LEGACY.filter((slug) =>
      slug === "ga"
        ? fundingApplication.selectedConnectorSlugs.includes(slug) &&
          isGoogleAnalyticsRequired(fundingApplication.selectedConnectorSlugs)
        : fundingApplication.selectedConnectorSlugs.includes(slug)
    );
    if (requiredPlatforms.length) {
      journey.push({ page: PAGE_TYPE.CONNECTORS, steps: requiredPlatforms });
    }
  }

  journey.push({ page: PAGE_TYPE.SUMMARY });

  return { navigationFlow: journey, totalSteps: journey.length - NOT_COUNTED_PAGES_LEGACY };
};

export const calculateJourney = (
  businessModel: BusinessModelData,
  countryOfIncorporation?: string,
  fundingApplication?: FormattedFundingApplicationScreen,
  uploadedDocuments?: FundingApplicationSchemas.ListDocumentsResponse[]
): NavigationContext => {
  if (!fundingApplication) {
    return { navigationFlow: [], totalSteps: 0 };
  }

  if (isRssBusinessModel(businessModel)) {
    return calculateRssJourney(fundingApplication, businessModel, countryOfIncorporation, uploadedDocuments);
  }
  return calculateEcommerceOrWholesaleJourney(
    fundingApplication,
    businessModel,
    countryOfIncorporation,
    uploadedDocuments
  );
};

export interface NavigationContext {
  navigationFlow: ApplicationJourneyPage[];
  totalSteps: number;
}

export const useNavigationContext = (): NavigationContext => {
  const context = useContext(LinearNavigationContext);

  if (!context) {
    throw Error("LinearNavigationContext used outside LinearNavigationContextProvider");
  }
  return context as NavigationContext;
};
